import { createTheme } from "@mui/material/styles";
import {
  BORDER_COLORS,
  BUTTON_COLORS,
  FONT_COLORS,
  MAIN_FONT,
  TOOLTIP_COLORS,
} from "./constants";

declare module "@mui/material/InputBase" {
  interface InputBasePropsSizeOverrides {
    small: true;
    medium: true;
    large: true;
  }
}

declare module "@mui/material/Button" {
  interface ButtonPropsColorOverrides {
    default: true;
  }
}

const theme = createTheme({
  typography: {
    fontFamily: MAIN_FONT,
  },
  palette: {
    primary: { main: BUTTON_COLORS.primary },
  },
  zIndex: {
    appBar: 1249,
    modal: 1248,
    drawer: 1251,
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        "@global": {
          body: {
            fontFamily: MAIN_FONT,
          },
        },
      },
    },
    MuiTypography: {
      styleOverrides: {
        root: {
          lineHeight: 1.5,
        },
      },
      variants: [
        {
          props: { variant: "h1" },
          style: {
            fontSize: "20px",
            fontWeight: 600,
          },
        },
        {
          props: { variant: "h2" },
          style: {
            fontSize: "18px",
            fontWeight: 600,
          },
        },
        {
          props: { variant: "h3" },
          style: {
            fontSize: "16px",
            fontWeight: 600,
          },
        },
        {
          props: { variant: "h4" },
          style: {
            fontSize: "14px",
            fontWeight: 600,
          },
        },
        {
          props: { variant: "subtitle1" },
          style: {
            fontSize: "12px",
            fontWeight: 600,
          },
        },
        {
          props: { variant: "subtitle2" },
          style: {},
        },
        {
          props: { variant: "body1" },
          style: {
            fontSize: "12px",
            fontWeight: 400,
          },
        },
        {
          props: { variant: "body2" },
          style: {
            fontSize: "11px",
            fontWeight: 400,
          },
        },
        {
          props: { variant: "caption" },
          style: {
            fontSize: "10px",
            fontWeight: 400,
          },
        },
      ],
    },
    MuiIconButton: {
      variants: [
        {
          props: { size: "small" },
          style: {
            width: "27px",
            height: "27px",
          },
        },
        {
          props: { size: "medium" },
          style: {
            width: "28px",
            height: "28px",
          },
        },
        {
          props: { size: "large" },
          style: {
            width: "32px",
            height: "32px",
          },
        },
      ],
    },
    MuiButton: {
      styleOverrides: {
        root: {
          fontSize: "12px",
          lineHeight: "18px",
          fontWeight: 500,
          borderRadius: "4px",
          textTransform: "none",
          paddingLeft: "8px",
          paddingRight: "8px",
          minWidth: 0,
        },
      },
      variants: [
        {
          props: { variant: "contained", color: "primary" },
          style: {
            backgroundColor: BUTTON_COLORS.primary,
            borderColor: BUTTON_COLORS.primaryBorder,
            color: BUTTON_COLORS.primaryText,
            "&:hover": {
              color: BUTTON_COLORS.primaryText,
              backgroundColor: BUTTON_COLORS.primaryHover,
            },
            "&:active": {
              color: BUTTON_COLORS.primaryText,
              backgroundColor: BUTTON_COLORS.primaryHover,
            },
          },
        },
        {
          props: { variant: "outlined", color: "primary" },
          style: {
            backgroundColor: BUTTON_COLORS.secondary,
            borderColor: BUTTON_COLORS.secondaryBorder,
            color: BUTTON_COLORS.secondaryText,
            paddingLeft: "7px",
            paddingRight: "7px",
            "&:hover": {
              color: BUTTON_COLORS.secondaryText,
              backgroundColor: BUTTON_COLORS.secondaryHover,
            },
            "&:active": {
              color: BUTTON_COLORS.secondaryText,
              backgroundColor: BUTTON_COLORS.secondaryHover,
            },
          },
        },
        {
          props: { variant: "contained", color: "default" },
          style: {
            backgroundColor: BUTTON_COLORS.default,
            borderColor: BUTTON_COLORS.defaultBorder,
            color: BUTTON_COLORS.defaultText,
            "&:hover": {
              color: BUTTON_COLORS.defaultText,
              backgroundColor: BUTTON_COLORS.defaultHover,
            },
            "&:active": {
              color: BUTTON_COLORS.defaultText,
              backgroundColor: BUTTON_COLORS.defaultHover,
            },
          },
        },
        {
          props: { variant: "outlined", color: "default" },
          style: {
            backgroundColor: "#FFF",
            borderColor: BUTTON_COLORS.defaultBorder,
            color: BUTTON_COLORS.defaultText,
            paddingLeft: "7px",
            paddingRight: "7px",
            "&:hover": {
              color: BUTTON_COLORS.defaultText,
              backgroundColor: BUTTON_COLORS.defaultHover,
            },
            "&:active": {
              color: BUTTON_COLORS.defaultText,
              backgroundColor: BUTTON_COLORS.defaultHover,
            },
          },
        },
        {
          props: { size: "small" },
          style: { height: "24px" },
        },
        {
          props: { size: "medium" },
          style: { height: "28px" },
        },
        {
          props: { size: "large" },
          style: { height: "32px" },
        },
      ],
    },
    MuiTabs: {
      styleOverrides: {
        root: {
          minHeight: "auto",
          borderBottom: "2px solid #F2F2F2",
          "& .MuiTabs-indicator": {
            backgroundColor: "#4C4C4C",
          },
          "& .MuiTabs-indicatorSpan": {
            width: "100%",
            backgroundColor: "#4C4C4C",
          },
        },
        scrollButtons: {
          "&.Mui-disabled": {
            display: "none",
          },
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: "none",
          minWidth: "92px",
          minHeight: "30px",
          color: "#00000D",
          fontSize: "10px",
          fontWeight: 400,
          lineHeight: "100%",
          letterSpacing: 0,
          border: "1px solid #E8E8E8",
          borderTopLeftRadius: "8px",
          borderTopRightRadius: "8px",
          borderBottom: "none",
          backgroundColor: "#F7F7F7",
          margin: 0,
          padding: "0 16px",
          "&:hover": {
            color: "#00000D",
            border: "1px solid #BABFC7",
            borderBottom: "none",
            opacity: 1,
            backgroundColor: "#FFFFFF",
          },
          "&.Mui-selected": {
            color: "#00000D",
            border: "1px solid #BABFC7",
            borderBottom: "none",
            fontWeight: 600,
            backgroundColor: "#FFFFFF",
          },
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          border: "1px solid #181D1F26",
          boxShadow: "0px 4px 4px 0px #00000040",
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          fontSize: "12px",
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: BORDER_COLORS.default,
          fontSize: "12px",
          color: FONT_COLORS.default,
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: TOOLTIP_COLORS.background,
        },
        arrow: {
          color: TOOLTIP_COLORS.background,
        },
      },
    },
    MuiPagination: {
      variants: [
        {
          props: { variant: "text", shape: "rounded", size: "small" },
          style: {
            button: {
              fontSize: "12px",
              minWidth: "24px",
              height: "24px",
            },
          },
        },
      ],
    },
    MuiLink: {
      styleOverrides: {
        root: {
          "&:hover": {
            color: FONT_COLORS.info,
          },
        },
      },
    },
  },
});

export default theme;
